import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { URLService } from 'src/app/services/dynamic-url.service';
import { SettingsService } from '../../../services/settings.service';

/** GET/PATCH /tutor/institution/agent-settings. */
interface AgentSettings {
  /** Staff have set the agent up for this organization and it is on. */
  agentEnabled: boolean;
  autoJoinCalls: boolean;
}

/**
 * Settings → AI coworker, for organization admins: how the agent behaves in the
 * organization's spaces, and (as tabs) the services it can work with and the AI
 * credits it has used.
 *
 * Each toggle saves as soon as it changes, rather than through a Save button,
 * because it is one setting with nothing to review before it applies.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-settings-ai-coworker',
  templateUrl: './user-settings-ai-coworker.component.html',
  styleUrls: ['./user-settings-ai-coworker.component.scss'],
  // OnPush per project policy: every async callback calls markForCheck().
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsAiCoworkerComponent implements OnInit {
  @Input() isMobile = false;
  @Input() PANELS: any;
  @Output() goBack = new EventEmitter<void>();

  isLoading = true;
  loadFailed = false;
  agentEnabled = false;

  readonly form = new UntypedFormGroup({
    settings: new UntypedFormGroup({
      autoJoinCalls: new UntypedFormControl(false),
    }),
  });

  constructor(
    private readonly http: HttpClient,
    private readonly urlService: URLService,
    private readonly settingsService: SettingsService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  private get autoJoinCalls(): UntypedFormControl {
    return this.form.get('settings.autoJoinCalls') as UntypedFormControl;
  }

  private get url(): string {
    return `${this.urlService.getDynamicUrl()}/tutor/institution/agent-settings`;
  }

  ngOnInit(): void {
    this.http
      .get<AgentSettings>(this.url)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (settings) => {
          this.apply(settings);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.loadFailed = true;
          this.cdr.markForCheck();
        },
      });

    this.autoJoinCalls.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: boolean) => this.save(value));
  }

  private apply(settings: AgentSettings): void {
    this.agentEnabled = settings.agentEnabled;
    this.autoJoinCalls.setValue(settings.autoJoinCalls, { emitEvent: false });
  }

  private save(autoJoinCalls: boolean): void {
    this.http
      .patch<AgentSettings>(this.url, { autoJoinCalls })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (settings) => {
          this.apply(settings);
          this.settingsService.showToast({
            title: this.translateService.instant('Settings updated'),
          });
          this.cdr.markForCheck();
        },
        error: () => {
          // Put the toggle back where the server still has it.
          this.autoJoinCalls.setValue(!autoJoinCalls, { emitEvent: false });
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
          this.cdr.markForCheck();
        },
      });
  }
}
